import { useSyncExternalStore } from "react";
import { DEFAULT_FONT, fontById, SYSTEM_FONT } from "./fonts.js";
import { PANEL_THEME, themeById } from "./themes.js";

/*
 * How the terminal looks, per person and browser: font, font size and colour
 * theme. Kept in localStorage (a private window may refuse it: the defaults
 * come back next time) and applied live to every open session
 * (`connections.ts` listens).
 */

export interface TerminalSettings {
  /** A font id of `fonts.ts`, or `system`. */
  font: string;
  fontSize: number;
  /** A theme id of `themes.ts`, or `panel` (follows the panel's light or dark look). */
  theme: string;
}

export const FONT_SIZES = { min: 10, max: 24, initial: 14 } as const;

export const DEFAULT_SETTINGS: TerminalSettings = {
  font: DEFAULT_FONT,
  fontSize: FONT_SIZES.initial,
  theme: PANEL_THEME,
};

const KEY = "cs.terminal.settings";
/** Before the settings dialog only the size was kept, under its own key. */
const LEGACY_FONT_SIZE_KEY = "cs.terminal.fontSize";

/** What `localStorage` offers; injected in tests. */
export interface SettingsStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

const browserStorage = (): SettingsStorage | null => {
  try {
    return localStorage;
  } catch {
    return null;
  }
};

export const clampFontSize = (size: number): number =>
  Number.isFinite(size)
    ? Math.min(FONT_SIZES.max, Math.max(FONT_SIZES.min, Math.round(size)))
    : FONT_SIZES.initial;

/** Settings from stored text; anything unknown or damaged falls back to the default. */
export function parseSettings(text: string | null, legacyFontSize: string | null = null) {
  let raw: Partial<Record<keyof TerminalSettings, unknown>> = {};
  try {
    const parsed: unknown = text ? JSON.parse(text) : {};
    if (parsed && typeof parsed === "object") raw = parsed as typeof raw;
  } catch {
    // Damaged JSON: the defaults.
  }
  const font =
    typeof raw.font === "string" && (raw.font === SYSTEM_FONT || fontById(raw.font))
      ? raw.font
      : DEFAULT_SETTINGS.font;
  const theme =
    typeof raw.theme === "string" && (raw.theme === PANEL_THEME || themeById(raw.theme))
      ? raw.theme
      : DEFAULT_SETTINGS.theme;
  const size =
    typeof raw.fontSize === "number" ? raw.fontSize : Number(legacyFontSize) || FONT_SIZES.initial;
  return { font, theme, fontSize: clampFontSize(size) } satisfies TerminalSettings;
}

export class TerminalSettingsStore {
  private state: TerminalSettings;
  private readonly listeners = new Set<() => void>();

  constructor(private readonly storage: SettingsStorage | null = browserStorage()) {
    this.state = parseSettings(this.read(KEY), this.read(LEGACY_FONT_SIZE_KEY));
  }

  private read(key: string): string | null {
    try {
      return this.storage?.getItem(key) ?? null;
    } catch {
      return null;
    }
  }

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly snapshot = (): TerminalSettings => this.state;

  update(patch: Partial<TerminalSettings>): void {
    const next = parseSettings(JSON.stringify({ ...this.state, ...patch }));
    if (
      next.font === this.state.font &&
      next.fontSize === this.state.fontSize &&
      next.theme === this.state.theme
    )
      return;
    this.state = next;
    try {
      this.storage?.setItem(KEY, JSON.stringify(next));
    } catch {
      // Storage refused (private window, full): the change holds until the tab closes.
    }
    for (const listener of this.listeners) listener();
  }

  reset(): void {
    this.update(DEFAULT_SETTINGS);
  }
}

/** The page's one settings store. */
export const terminalSettings = new TerminalSettingsStore();

export function useTerminalSettings(): TerminalSettings {
  return useSyncExternalStore(
    terminalSettings.subscribe,
    terminalSettings.snapshot,
    terminalSettings.snapshot,
  );
}
